import React from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  CircularProgress,
  FormHelperText,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import {
  RegisterReviewSection,
  RegisterReviewSectionTitle,
} from "../../../../Account/Register/Register.style";
import { AideLeasingAckData, emptyLiveInAideState } from "../../liveInAideState";

// ---------------------------------------------------------------------------
// Screen 8 — Leasing acknowledgements (RESPROP LIA Questionnaire p3-4, "Leasing
// Information"). Six Yes/No questions, all required. Reuses the affordable Y/N
// ToggleButton pattern (see AideHousehold): booleans stored as boolean|null,
// `null` == unanswered.
// ---------------------------------------------------------------------------

interface Props {
  /** Persisted leasing-ack slice (state.leasingAck). */
  defaultValues?: Partial<AideLeasingAckData>;
  onSubmit: (data: AideLeasingAckData) => void | Promise<void>;
  isSaving?: boolean;
}

/** The six Y/N leasing questions, in questionnaire order. */
const QUESTION_KEYS: (keyof AideLeasingAckData)[] = [
  "readLease",
  "agreeAbide",
  "understandEviction",
  "noOccupancyRights",
  "vacateIfAbsent",
  "vacateOnDeath",
];

export const AideLeasingAck: React.FC<Props> = ({
  defaultValues,
  onSubmit,
  isSaving = false,
}) => {
  const { t } = useTranslation();

  const requiredMsg = t(
    "affordable.application.liveInAide.leasingAck.required"
  ) as string;

  const schema = yup.object({
    readLease: yup.boolean().nullable().required(requiredMsg),
    agreeAbide: yup.boolean().nullable().required(requiredMsg),
    understandEviction: yup.boolean().nullable().required(requiredMsg),
    noOccupancyRights: yup.boolean().nullable().required(requiredMsg),
    vacateIfAbsent: yup.boolean().nullable().required(requiredMsg),
    vacateOnDeath: yup.boolean().nullable().required(requiredMsg),
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<AideLeasingAckData>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      readLease:
        defaultValues?.readLease ?? emptyLiveInAideState.leasingAck.readLease,
      agreeAbide:
        defaultValues?.agreeAbide ?? emptyLiveInAideState.leasingAck.agreeAbide,
      understandEviction:
        defaultValues?.understandEviction ??
        emptyLiveInAideState.leasingAck.understandEviction,
      noOccupancyRights:
        defaultValues?.noOccupancyRights ??
        emptyLiveInAideState.leasingAck.noOccupancyRights,
      vacateIfAbsent:
        defaultValues?.vacateIfAbsent ??
        emptyLiveInAideState.leasingAck.vacateIfAbsent,
      vacateOnDeath:
        defaultValues?.vacateOnDeath ??
        emptyLiveInAideState.leasingAck.vacateOnDeath,
    },
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <RegisterReviewSection>
        <RegisterReviewSectionTitle>
          {t("affordable.application.liveInAide.leasingAck.title")}
        </RegisterReviewSectionTitle>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {t("affordable.application.liveInAide.leasingAck.subtitle")}
        </Typography>

        {QUESTION_KEYS.map((key) => (
          <Controller
            key={key}
            name={key}
            control={control}
            render={({ field }) => (
              <Box sx={{ mb: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                    flexWrap: "wrap",
                  }}
                >
                  <Typography variant="body1" sx={{ flex: 1, minWidth: 0 }}>
                    {t(
                      `affordable.application.liveInAide.leasingAck.questions.${key}`
                    )}
                  </Typography>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={
                      field.value === true
                        ? "yes"
                        : field.value === false
                        ? "no"
                        : null
                    }
                    onChange={(_, val) => {
                      if (val !== null) field.onChange(val === "yes");
                    }}
                    sx={{ flexShrink: 0 }}
                  >
                    <ToggleButton value="yes" sx={{ px: 2 }}>
                      {t("affordable.application.liveInAide.leasingAck.yes")}
                    </ToggleButton>
                    <ToggleButton value="no" sx={{ px: 2 }}>
                      {t("affordable.application.liveInAide.leasingAck.no")}
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
                {errors[key] && (
                  <FormHelperText error>
                    {errors[key]?.message as string}
                  </FormHelperText>
                )}
              </Box>
            )}
          />
        ))}
      </RegisterReviewSection>

      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3, mb: 4 }}>
        <Button
          variant="contained"
          type="submit"
          disabled={isSaving}
          startIcon={
            isSaving ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          {t("account.leaseApplication.next")}
        </Button>
      </Box>
    </form>
  );
};
